import { useEffect, useState } from 'react';
import { CheckCircleIcon, PhotoIcon, XMarkIcon, PencilIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { casesApi, Case, CaseNote } from '../services/api';

const API_URL = import.meta.env.VITE_API_URL || '';

const STATUSES = ['all', 'open', 'in_progress', 'resolved'];
const SEVERITIES = ['all', 'low', 'high', 'critical'];

export default function Cases() {
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [caseNotes, setCaseNotes] = useState('');
  const [notesHistory, setNotesHistory] = useState<CaseNote[]>([]);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [noteSaveSuccess, setNoteSaveSuccess] = useState(false);

  // Resolve modal state
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const casesData = await casesApi.list();
      setCases(casesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cases');
    } finally {
      setIsLoading(false);
    }
  }

  async function openDetailModal(caseItem: Case) {
    setSelectedCase(caseItem);
    setCaseNotes('');
    setNotesHistory([]);
    setShowDetailModal(true);

    // Fetch notes history
    setIsLoadingNotes(true);
    try {
      const notes = await casesApi.getNotes(caseItem.id);
      setNotesHistory(notes);
    } catch (err) {
      console.error('Failed to load notes history:', err);
    } finally {
      setIsLoadingNotes(false);
    }
  }

  function closeDetailModal() {
    setShowDetailModal(false);
    setSelectedCase(null);
  }

  function openResolveModal(caseItem: Case) {
    setSelectedCase(caseItem);
    setResolutionNotes('');
    setShowResolveModal(true);
    setShowDetailModal(false);
  }

  async function handleSaveNotes() {
    if (!selectedCase || !caseNotes.trim()) return;
    setIsSavingNotes(true);
    setNoteSaveSuccess(false);
    try {
      const newNote = await casesApi.addNote(selectedCase.id, caseNotes);
      // Add new note to history at the top
      setNotesHistory([newNote, ...notesHistory]);
      // Clear the input
      setCaseNotes('');
      // Update local case state
      const updatedCases = cases.map(c =>
        c.id === selectedCase.id ? { ...c, notes: newNote.note } : c
      );
      setCases(updatedCases);
      // Show success feedback
      setNoteSaveSuccess(true);
      setTimeout(() => setNoteSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save note:', err);
      alert(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setIsSavingNotes(false);
    }
  }

  async function handleResolve() {
    if (!selectedCase) return;

    setIsResolving(true);
    try {
      await casesApi.resolve(selectedCase.id, resolutionNotes);
      await loadData();
      setShowResolveModal(false);
      setShowDetailModal(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to resolve case');
    } finally {
      setIsResolving(false);
    }
  }

  const filteredCases = cases.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (severityFilter !== 'all' && c.severity !== severityFilter) return false;
    return true;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPhotoUrl = (photoUrl: string) => {
    if (!photoUrl) return '';
    // photoUrl is the full R2 key like "photos/{answerId}/{photoId}.jpg"
    return `${API_URL}/api/photos/${photoUrl}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Cases</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage store visit cases
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Statuses' : status.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {severity === 'all' ? 'All Severities' : severity}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{cases.length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <p className="text-sm text-gray-500">Open</p>
          <p className="text-2xl font-bold text-orange-600">
            {cases.filter(c => c.status === 'open').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <p className="text-sm text-gray-500">In Progress</p>
          <p className="text-2xl font-bold text-blue-600">
            {cases.filter(c => c.status === 'in_progress').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <p className="text-sm text-gray-500">Resolved</p>
          <p className="text-2xl font-bold text-green-600">
            {cases.filter(c => c.status === 'resolved').length}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Cases Table */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Store / Question
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Severity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Due Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Photos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCases.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  No cases found matching the selected filters.
                </td>
              </tr>
            ) : (
              filteredCases.map((caseItem) => (
                <tr
                  key={caseItem.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openDetailModal(caseItem)}
                >
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {caseItem.store_name || 'Unknown Store'}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {caseItem.question_text || 'No question text'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(caseItem.severity)}`}>
                      {caseItem.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(caseItem.status)}`}>
                      {caseItem.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {formatDate(caseItem.deadline_at)}
                    </div>
                    {caseItem.is_overdue && (
                      <span className="text-xs text-red-600 font-medium">Overdue</span>
                    )}
                    {!caseItem.is_overdue && caseItem.days_remaining !== undefined && caseItem.days_remaining <= 1 && (
                      <span className="text-xs text-orange-600 font-medium">Due soon</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {caseItem.photos && caseItem.photos.length > 0 ? (
                      <div className="flex items-center gap-1">
                        <PhotoIcon className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-blue-600">{caseItem.photos.length}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {caseItem.assignee_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {caseItem.status !== 'resolved' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openResolveModal(caseItem);
                        }}
                        className="inline-flex items-center text-green-600 hover:text-green-900"
                      >
                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                        Resolve
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Case Detail Modal */}
      {showDetailModal && selectedCase && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={closeDetailModal} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Case Details</h2>
                  <p className="text-sm text-gray-500">{selectedCase.store_name}</p>
                </div>
                <button onClick={closeDetailModal} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Question */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Question</h3>
                  <p className="text-gray-900">{selectedCase.question_text}</p>
                </div>

                {/* Status & Severity */}
                <div className="flex gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Status</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedCase.status)}`}>
                      {selectedCase.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Severity</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(selectedCase.severity)}`}>
                      {selectedCase.severity}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Due Date</h3>
                    <p className={`text-sm ${selectedCase.is_overdue ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                      {formatDate(selectedCase.deadline_at)}
                      {selectedCase.is_overdue && ' (Overdue)'}
                    </p>
                  </div>
                </div>

                {/* Assigned To / Reported By */}
                <div className="flex gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Assigned To</h3>
                    <p className="text-gray-900">{selectedCase.assignee_name || '-'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Reported By</h3>
                    <p className="text-gray-900">{selectedCase.reporter_name || '-'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Created</h3>
                    <p className="text-gray-900">{formatDate(selectedCase.created_at)}</p>
                  </div>
                </div>

                {/* Photos */}
                {selectedCase.photos && selectedCase.photos.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Photos</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {selectedCase.photos.map((photo, idx) => (
                        <a
                          key={idx}
                          href={getPhotoUrl(photo.photo_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity"
                        >
                          <img
                            src={getPhotoUrl(photo.photo_url)}
                            alt={`Case photo ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Add Note</h3>
                  {selectedCase.status !== 'resolved' && (
                    <>
                      <textarea
                        rows={3}
                        value={caseNotes}
                        onChange={(e) => setCaseNotes(e.target.value)}
                        placeholder="Add a note about this case..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <button
                        onClick={handleSaveNotes}
                        disabled={isSavingNotes || !caseNotes.trim()}
                        className={`mt-2 w-full px-4 py-3 text-sm font-medium text-white rounded-lg flex items-center justify-center gap-2 transition-colors ${
                          noteSaveSuccess
                            ? 'bg-green-500'
                            : 'bg-primary-500 hover:bg-primary-600 disabled:opacity-50'
                        }`}
                      >
                        {noteSaveSuccess ? (
                          <>
                            <CheckCircleIcon className="h-5 w-5" />
                            Note Saved!
                          </>
                        ) : isSavingNotes ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <PencilIcon className="h-5 w-5" />
                            Save Note
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {/* Notes History */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                      <ChatBubbleLeftIcon className="h-4 w-4" />
                      Notes History
                    </h4>
                    {isLoadingNotes ? (
                      <div className="text-sm text-gray-400 text-center py-4">Loading notes...</div>
                    ) : notesHistory.length === 0 ? (
                      <div className="text-sm text-gray-400 text-center py-4">No notes yet</div>
                    ) : (
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {notesHistory.map((note) => (
                          <div key={note.id} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-700">{note.user_name}</span>
                              <span className="text-xs text-gray-400">
                                {new Date(note.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{note.note}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Resolution (if resolved) */}
                {selectedCase.status === 'resolved' && selectedCase.resolution_comment && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-green-800 mb-1">Resolution</h3>
                    <p className="text-green-700">{selectedCase.resolution_comment}</p>
                    <p className="text-xs text-green-600 mt-2">
                      Resolved on {formatDate(selectedCase.resolved_at)}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              {selectedCase.status !== 'resolved' && (
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={closeDetailModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => openResolveModal(selectedCase)}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                    Resolve Case
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && selectedCase && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowResolveModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Resolve Case
              </h2>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{selectedCase.store_name}</p>
                <p className="text-sm text-gray-500">{selectedCase.question_text}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resolution Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe how the issue was resolved (min 10 characters)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowResolveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResolve}
                  disabled={isResolving || resolutionNotes.length < 10}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {isResolving ? 'Resolving...' : 'Mark Resolved'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
